import { Injectable, NgZone } from '@angular/core';
import { Participant, Room, RoomEvent } from 'livekit-client';
import { ChatMessage } from '../calling-components/chat-panel/chat-panel.component';
import { CallSocketService } from './call-socket.service';

@Injectable({ providedIn: 'root' })
export class VideoCallService {
  room: Room | null = null;
  isActive = false;
  isMinimized = false;
  roomName = '';
  username = '';
  consultationData: any = null;
  callType: 'audio' | 'video' = 'video';
  callDuration = 0;
  allParticipants: Participant[] = [];
  chatMessages: ChatMessage[] = [];

  private durationInterval?: ReturnType<typeof setInterval>;

  constructor(
    private ngZone: NgZone,
    private callSocketService: CallSocketService,
  ) {}

  register(
    room: Room,
    roomName: string,
    username: string,
    consultationData: any,
    callType: 'audio' | 'video' = 'video',
  ): void {
    this.room = room;
    this.roomName = roomName;
    this.username = username;
    this.consultationData = consultationData;
    this.callType = callType;
    this.isActive = true;
    this.isMinimized = false;
    this.syncParticipants();
    this.attachRoomListeners();
  }

  private attachRoomListeners(): void {
    if (!this.room) return;
    this.room.on(RoomEvent.ParticipantConnected, this.syncParticipants);
    this.room.on(RoomEvent.ParticipantDisconnected, this.syncParticipants);
    this.room.on(RoomEvent.TrackSubscribed, this.syncParticipants);
    this.room.on(RoomEvent.TrackUnsubscribed, this.syncParticipants);
    this.room.on(RoomEvent.TrackMuted, this.syncParticipants);
    this.room.on(RoomEvent.TrackUnmuted, this.syncParticipants);
    this.room.on(RoomEvent.Disconnected, this.onDisconnected);
  }

  detachRoomListeners(): void {
    if (!this.room) return;
    this.room.off(RoomEvent.ParticipantConnected, this.syncParticipants);
    this.room.off(RoomEvent.ParticipantDisconnected, this.syncParticipants);
    this.room.off(RoomEvent.TrackSubscribed, this.syncParticipants);
    this.room.off(RoomEvent.TrackUnsubscribed, this.syncParticipants);
    this.room.off(RoomEvent.TrackMuted, this.syncParticipants);
    this.room.off(RoomEvent.TrackUnmuted, this.syncParticipants);
    this.room.off(RoomEvent.Disconnected, this.onDisconnected);
  }

  private syncParticipants = (): void => {
    if (!this.room) return;
    this.ngZone.run(() => {
      this.allParticipants = [
        this.room!.localParticipant,
        ...Array.from(this.room!.remoteParticipants.values()),
      ];
    });
  };

  private onDisconnected = (): void => {
    this.ngZone.run(() => this.reset());
  };

  startTimer(): void {
    this.callDuration = 0;
    clearInterval(this.durationInterval);
    this.durationInterval = setInterval(() => {
      this.ngZone.run(() => {
        this.callDuration++;
      });
    }, 1000);
  }

  stopTimer(): void {
    clearInterval(this.durationInterval);
    this.durationInterval = undefined;
  }

  getFormattedDuration(): string {
    const m = Math.floor(this.callDuration / 60);
    const s = this.callDuration % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  minimize(): void {
    this.isMinimized = true;
  }

  restore(): void {
    this.isMinimized = false;
  }

  reset(): void {
    this.stopTimer();
    this.detachRoomListeners();
    this.room = null;
    this.isActive = false;
    this.isMinimized = false;
    this.roomName = '';
    this.callType = 'video';
    this.callDuration = 0;
    this.allParticipants = [];
    this.chatMessages = [];
    this.consultationData = null;
    // Single choke point for every disconnect path (explicit leave, PiP leave,
    // remote hangup while minimized) — without this, inCall can get stuck
    // true and silently block all future incoming calls until a page refresh.
    this.callSocketService.setInCallState(false);
  }
}
